/* eslint-disable jsx-a11y/role-supports-aria-props */
import { Box, Tab, Tooltip } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { OptionalTooltipWrapper } from '../Tooltip'

const useStyles = makeStyles()(theme => ({
  tab: {
    textAlign: 'center',
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    marginRight: 0,
    opacity: 1,
    '&.MuiTab-root:last-of-type': {
      marginRight: 0,
    },
    whiteSpace: 'nowrap',
  },
  activeTab: {
    color: theme.palette.primary.dark,
  },
  inactiveTab: {
    color: theme.palette.text.primary,
  },
  tabDisabledLook: {
    color: theme.palette.grey.grey05,
  },
  link: {
    textDecoration: 'none',
  },
}))

export interface LinkedTooltipTabProps {
  id: string
  onClick?: (href?: string) => void
  label: string | React.ReactNode
  icon?: React.ReactNode
  disabled?: boolean
  disabledTooltipText?: string
  href?: string
  isActive?: boolean
  hasDisabledStyleAlways?: boolean
  dataQa?: string
}
/**
 * LinkedTooltipTab : provide tab with several options and
 * stop event propagation when there is a link wrapping a clickable element
 * @returns tab with tooltip but without link
 * @returns tab with tooltip and link
 * @returns tab with link but without tooltip
 * @returns defult tab
 * * */
const LinkedTooltipTab: React.FC<LinkedTooltipTabProps> = ({
  id,
  onClick,
  label,
  icon,
  disabled,
  disabledTooltipText,
  href,
  isActive = false,
  hasDisabledStyleAlways = false,
  dataQa,
}) => {
  const { cx, classes } = useStyles()

  const handleClick = useCallback(
    /** TODO: add correct event type */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      event.stopPropagation()
      if (onClick) {
        onClick(href)
      }
    },
    [href, onClick]
  )

  const withTooltip = (child: React.ReactElement, text: string): React.ReactElement => (
    <Tooltip title={text} enterTouchDelay={0}>
      <Box sx={{ px: 2 }}>{child}</Box>
    </Tooltip>
  )
  const defaultTab = (
    <Tab id={id} component="div" label={label} disabled={disabled} className={classes.tab} onClick={handleClick} wrapped data-qa={dataQa} />
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  /**
   * when the LinkedTab is disabled only normal table is allowed
   * */
  const linkedTab = useMemo(() => {
    /* TODO: we can not just put a link in here, because this results in a hard refresh of the web application. disabled until a proper solution is found */
    if (href && !disabled && hasDisabledStyleAlways) {
      return (
        <OptionalTooltipWrapper titleKey={hasDisabledStyleAlways ? disabledTooltipText : ''}>
          <Link id={id} to={href} className={cx(classes.link, classes.tabDisabledLook)} aria-selected={isActive} role={'link'}>
            <Tab component="div" label={label} disabled={disabled} className={classes.tab} wrapped data-qa={dataQa} />
          </Link>
        </OptionalTooltipWrapper>
      )
    }

    if (href && !disabled) {
      /* TODO: we can not just put a link in here, because this results in a hard refresh of the web application. disabled until a proper solution is found */
      return (
        <Link
          id={id}
          to={href}
          className={cx(classes.link, { [classes.activeTab]: isActive, [classes.inactiveTab]: !isActive })}
          aria-selected={isActive}
          role={'link'}
        >
          <Tab component="div" label={label} disabled={disabled} className={classes.tab} wrapped data-qa={dataQa} />
        </Link>
      )
    }

    return defaultTab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [href, label, disabled, isActive, hasDisabledStyleAlways])

  if (href && !disabled) {
    return linkedTab
  }

  if (disabledTooltipText && disabled) {
    return withTooltip(defaultTab, disabledTooltipText)
  }

  return defaultTab
}
export default LinkedTooltipTab
